import React from 'react';
import TimePicker from 'react-time-picker';
import styled from 'styled-components';
import { AppButton } from '../buttons';
import { calculateNextValidTime } from './utils';

const TimePickerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--medium-padding);
  border-top: 1px solid var(--gray-dark);
  margin-top: var(--small-padding);

  .time-controls {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .arrows-section {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: var(--xsmall-padding);
  }

  .time-arrow {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: var(--black-main);
    display: flex;
    align-items: center;
    justify-content: center;
    height: 14px;

    &:hover {
      color: var(--red-main);
    }
  }

  .react-time-picker {
    .react-time-picker__wrapper {
      background-color: var(--beige-main);
      border-radius: var(--default-border-radius);
      border: 1px solid var(--beige-main);
      padding: var(--small-padding) var(--default-padding);

      .react-time-picker__inputGroup {
        font-family: var(--font-family);
        font-size: var(--small-font-size);
        line-height: var(--small-line-height);
        color: var(--black-main);
        text-align: center;

        input {
          color: var(--black-main);

          &:focus {
            outline: none;
          }
        }

        .react-time-picker__inputGroup__input--hasLeadingZero {
          background-color: transparent;
        }

        .react-time-picker__inputGroup__leadingZero {
          padding-inline: var(--xxsmall-padding) !important;
        }
        .react-time-picker__inputGroup__hour {
          padding-inline: var(--xxsmall-padding) !important;
        }

        .react-time-picker__inputGroup__minute {
          padding-left: var(--xxsmall-padding) !important;
        }
      }

      &:focus-within {
        border-color: var(--red-main);
        box-shadow: var(--box-shadow-red-no-blur);
      }
    }

    .react-time-picker__button {
      padding: 0 var(--small-padding);

      &:hover {
        svg {
          stroke: var(--red-main);
        }
      }

      svg {
        stroke: var(--black-main);
      }
    }
  }
`;

const HoursOnlyContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--small-padding);
  padding: var(--medium-padding);

  .hour-display {
    font-family: var(--font-family);
    font-size: var(--small-font-size);
    background-color: var(--beige-main);
    border-radius: var(--default-border-radius);
    padding: var(--small-padding) var(--default-padding);
    min-width: 52px;
    text-align: center;
  }

  .time-arrow {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: var(--black-main);
    display: flex;
    align-items: center;
    justify-content: center;
    height: 14px;

    &:hover {
      color: var(--red-main);
    }
  }
`;

type TimeControlsProps = {
  value: Date | null;
  onChange: (value: Date | null) => void;
  stepMinute?: number;
  minDate?: Date;
  maxDate?: Date;
  hoursOnly?: boolean;
};

export const TimeControls: React.FC<TimeControlsProps> = ({
  value,
  onChange,
  stepMinute = 1,
  minDate,
  maxDate,
  hoursOnly = false,
}) => {
  const hours = value ? value.getHours() : 0;
  const minutes = value ? value.getMinutes() : 0;

  const isDateDisabled = (date: Date): boolean => {
    if (!minDate && !maxDate) return false;

    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;

    return false;
  };

  const isTimeBeforeMinDate = (
    testHours: number,
    testMinutes: number,
  ): boolean => {
    if (!minDate || !value) return false;

    const testDate = new Date(value);
    testDate.setHours(testHours);
    testDate.setMinutes(testMinutes);
    testDate.setSeconds(0);
    testDate.setMilliseconds(0);

    return isDateDisabled(testDate);
  };

  const updateTime = (newHours: number, newMinutes: number) => {
    const newDate = value ? new Date(value) : new Date();

    const adjustedTime = calculateNextValidTime(
      newHours,
      newMinutes,
      stepMinute,
    );

    newDate.setHours(adjustedTime.hours);
    newDate.setMinutes(adjustedTime.minutes);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);

    if (!isDateDisabled(newDate)) {
      onChange?.(newDate);
    }
  };

  const incrementHours = () => {
    const newHours = (hours + 1) % 24;
    updateTime(newHours, minutes);
  };

  const decrementHours = () => {
    const newHours = (hours - 1 + 24) % 24;
    updateTime(newHours, minutes);
  };

  const incrementMinutes = () => {
    const totalMinutes = hours * 60 + minutes + stepMinute;
    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMinutes = totalMinutes % 60;
    updateTime(newHours, newMinutes);
  };

  const decrementMinutes = () => {
    const totalMinutes = hours * 60 + minutes - stepMinute;
    const adjustedTotalMinutes =
      totalMinutes < 0 ? totalMinutes + 24 * 60 : totalMinutes;
    const newHours = Math.floor(adjustedTotalMinutes / 60) % 24;
    const newMinutes = adjustedTotalMinutes % 60;
    updateTime(newHours, newMinutes);
  };

  const formatTimeFromDate = (date: Date | null | undefined): string => {
    if (!date) return '00:00';

    const adjustedTime = calculateNextValidTime(
      date.getHours(),
      date.getMinutes(),
      stepMinute,
    );
    return `${String(adjustedTime.hours)}:${String(adjustedTime.minutes)}`;
  };

  const handleTimeChange = (timeValue: string | null) => {
    if (!timeValue) return;

    const timePattern = /^([0-9]{1,2}):([0-9]{1,2})$/;
    const match = timeValue.match(timePattern);

    if (!match) return;

    const [, hoursStr, minutesStr] = match;
    const newHours = parseInt(hoursStr, 10);
    const newMinutes = parseInt(minutesStr, 10);

    if (newHours >= 0 && newHours < 24 && newMinutes >= 0 && newMinutes < 60) {
      updateTime(newHours, newMinutes);
    }
  };

  if (hoursOnly) {
    return (
      <HoursOnlyContainer>
        <AppButton
          className="time-arrow"
          onClick={incrementHours}
          icon="pi pi-angle-up"
          severity="info"
          type="outlined"
          size="xs"
          aria-label="Increment hours"
          isDisabled={isTimeBeforeMinDate((hours + 1) % 24, 0)}
        />
        <span className="hour-display">
          {String(hours).padStart(2, '0')}:00
        </span>
        <AppButton
          className="time-arrow"
          onClick={decrementHours}
          icon="pi pi-angle-down"
          severity="info"
          type="outlined"
          size="xs"
          aria-label="Decrement hours"
          isDisabled={isTimeBeforeMinDate((hours - 1 + 24) % 24, 0)}
        />
      </HoursOnlyContainer>
    );
  }

  return (
    <TimePickerContainer>
      <div className="time-controls">
        <div className="arrows-section">
          <AppButton
            className="time-arrow"
            onClick={incrementHours}
            icon="pi pi-angle-up"
            severity="info"
            type="outlined"
            size="xs"
            aria-label="Increment hours"
            isDisabled={isTimeBeforeMinDate((hours + 1) % 24, minutes)}
          />
          <AppButton
            className="time-arrow"
            onClick={decrementHours}
            icon="pi pi-angle-down"
            severity="info"
            type="outlined"
            size="xs"
            aria-label="Decrement hours"
            isDisabled={isTimeBeforeMinDate((hours - 1 + 24) % 24, minutes)}
          />
        </div>

        <TimePicker
          value={formatTimeFromDate(value)}
          onChange={handleTimeChange}
          format="HH:mm"
          clearIcon={null}
          hourPlaceholder="hh"
          minutePlaceholder="mm"
          disableClock
          aria-label="Time picker"
        />

        <div className="arrows-section">
          <AppButton
            className="time-arrow"
            onClick={incrementMinutes}
            icon="pi pi-angle-up"
            severity="info"
            type="outlined"
            size="xs"
            aria-label="Increment minutes"
            isDisabled={(() => {
              const totalMinutes = hours * 60 + minutes + stepMinute;
              const newHours = Math.floor(totalMinutes / 60) % 24;
              const newMinutes = totalMinutes % 60;
              return isTimeBeforeMinDate(newHours, newMinutes);
            })()}
          />
          <AppButton
            className="time-arrow"
            onClick={decrementMinutes}
            icon="pi pi-angle-down"
            severity="info"
            type="outlined"
            size="xs"
            aria-label="Decrement minutes"
            isDisabled={(() => {
              const totalMinutes = hours * 60 + minutes - stepMinute;
              const adjustedTotalMinutes =
                totalMinutes < 0 ? totalMinutes + 24 * 60 : totalMinutes;
              const newHours = Math.floor(adjustedTotalMinutes / 60) % 24;
              const newMinutes = adjustedTotalMinutes % 60;
              return isTimeBeforeMinDate(newHours, newMinutes);
            })()}
          />
        </div>
      </div>
    </TimePickerContainer>
  );
};
